/* src/components/pages/Seguimiento.jsx - Última Modificación: 21-02-2026 */
/* ---------------------------------------------------------------------- */

import React, {
  useState,
  useCallback,
  useMemo,
} from 'react';

import {
  Box,
  TextField,
  Stack,
  Typography,
  Button,
  Checkbox
} from '@mui/material';

import api from '../../services/api.js'; // Api Interceptor en vez de Axios

import { DataGrid } from "@mui/x-data-grid";
import { esES } from "@mui/x-data-grid/locales";
import { CustomToolbar } from "../datagrid/CustomToolbar.jsx";
import DataGridSkeleton from "../datagrid/DataGridSkeleton.jsx";

import { normalizeRut } from "../utils/rutUtils.js";

import { useAuth } from "../../context/AuthContext.jsx"; // token

import { RiFileSearchLine } from "react-icons/ri";

/* Dialogo de confirmacion */
import { useFeedback } from '../../context/FeedbackContext.jsx'; // Mensajes breves


const Seguimiento = ({ modo }) => {
  const { user } = useAuth();

  const [datos, setDatos] = useState([]);
  const [loading, setLoading] = useState(false);

  const [savingRows, setSavingRows] = useState({});

  /* Estados para paginacón desde el servidor */
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);


  /* Confirmación */
  const { showFeedback } = useFeedback(); // Mensajes breves no bloqueantes


  /* Obtener Datos */
  const fetchDatos = async ({ page = 0, pageSize = 20 } = {}) => {
    if (!user.usuario) {
      showFeedback({
        message: 'Debe ingresar el Rut',
        severity: 'warning',
      });
      return;
    }

    setLoading(true);

    try {
      const RutCli = normalizeRut(user.usuario);

      const response = await api.post('/seguimiento/gettracking', {
        RutCli,
        page,
        pageSize,
      });

      if (response.data.success) {
        setDatos(response.data.rows || []);
        setRowCount(response.data.total || 0);

        if (!response.data.rows?.length) {
          showFeedback({
            message: 'No se encontraron registros',
            severity: 'info',
          });
        }
      }
    } catch (error) {
      setDatos([]);
      setRowCount(0);

      showFeedback({
        message: 'No fue posible obtener su solicitud',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  /* Manejador de checks */
  const handleToggle = useCallback(async (row, field) => {
    const key = row.id;

    const valorAnterior = row[field];
    const nuevoValor = valorAnterior === 1 ? 0 : 1;

    try {
      // 🔒 bloquear fila
      setSavingRows(prev => {
        if (prev[key]) return prev;
        return { ...prev, [key]: true };
      });

      // ⚡ optimistic update simple
      setDatos(prev =>
        prev.map(r =>
          r.id === row.id ? { ...r, [field]: nuevoValor } : r
        )
      );

      const response = await api.put('/seguimiento/updateflag', {
        id: row.id,
        field,
        value: nuevoValor,
      });

      // ⚠️ backend pudo corregir tracker
      if (response.data?.correctedRow) {
        setDatos(prev =>
          prev.map(r =>
            r.id === row.id ? { ...r, ...response.data.correctedRow } : r
          )
        );

        showFeedback({
          message: 'Tracker desactivado: debe seleccionar al menos un módulo.',
          severity: 'info',
        });
      }

    } catch (error) {
      // 🔙 rollback
      setDatos(prev =>
        prev.map(r =>
          r.id === row.id ? { ...r, [field]: valorAnterior } : r
        )
      );

      showFeedback({
        message: '❌ No se pudo actualizar',
        severity: 'error',
      });

    } finally {
      setSavingRows(prev => ({ ...prev, [key]: false }));
    }
  }, [showFeedback]);

  /* Render check reutilizable */
  const renderCheck = useCallback((params, field) => {
    const row = params.row;
    const isSaving = savingRows[row.id];

    return (
      <Box
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Checkbox
          checked={row[field] === 1}
          disabled={isSaving}
          onChange={() => handleToggle(row, field)}
          size="small"
        />
      </Box>
    );
  }, [savingRows, handleToggle]);


  /* MUI DataGrid */
  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'Id',
        width: 100,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'tipo',
        headerName: "Tipo",
        width: 100,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'rutcli',
        headerName: "Cliente",
        width: 130,
        align: 'center',
        headerAlign: 'center',
      },
      {
        field: 'rutenr',
        headerName: "Enrolado",
        width: 130,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => params.value || '—',
      },
      {
        field: "nombre",
        headerName: "Razón Social",
        width: 300,
        align: "center",
        headerAlign: "center",
      },
      {
        field: "tracker",
        headerName: "Tracker",
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => renderCheck(params, 'tracker'),
      },
      {
        field: "ventas",
        headerName: "Ventas",
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => renderCheck(params, 'ventas'),
      },
      {
        field: "compras",
        headerName: "Compras",
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => renderCheck(params, 'compras'),
      },
      {
        field: "deudor",
        headerName: "Deudor",
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => renderCheck(params, 'deudor'),
      },
      {
        field: "cedente",
        headerName: "Cedente",
        width: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => renderCheck(params, 'cedente'),
      },
      {
        field: "cesionario",
        headerName: "Cesionario",
        width: 120,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => renderCheck(params, 'cesionario'),
      },
      {
        field: "fecha",
        headerName: "Fecha",
        width: 200,
        align: "center",
        headerAlign: "center",
        renderCell: (params) => {
          if (!params.value) return "—";
          return new Date(params.value).toLocaleString("es-CL");
        },
      },
    ],
    [renderCheck]
  );



  /* Render de los datos */
  return (
    <Box sx={{ width: '100%', pt: 0, overflow: 'visible' }}>
      {/* Barra de Controles */}
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        alignItems="center"
        sx={{
          mb: 1,
          flexShrink: 0,       // 👈 NO se colapsa
        }}
      >
        <TextField
          label="Rut"
          value={user?.usuario || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: { shrink: true },
          }}
          sx={{
            minWidth: 140,
            flexShrink: 0,

            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        <TextField
          label="datos"
          value={user?.nombre || ""}
          size="small"
          disabled
          slotProps={{
            inputLabel: {
              shrink: true,
            },
          }}
          sx={{
            minWidth: 260,
            flexGrow: 1,
            flexShrink: 0,

            "& .MuiInputBase-root": {
              backgroundColor: (theme) => theme.custom.ui.readonlyBg,
            },

            "& .MuiInputBase-input.Mui-disabled": {
              WebkitTextFillColor: (theme) => theme.custom.ui.readonlyText,
            },

            "& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-disabled": {
              color: (theme) => theme.custom.ui.readonlyLabel,
            },
          }}
        />

        {/* Botón Buscar */}
        <Button
          variant="contained"
          startIcon={<RiFileSearchLine size={18} />}
          onClick={() => {
            setPage(0);
            setRowCount(0);
            setDatos([]);
            fetchDatos({ page: 0, pageSize });
          }}
          sx={{
            minWidth: 110,
            height: 40,
            flexShrink: 0,

            backgroundColor: (theme) => theme.custom.ui.primaryAction,
            color: (theme) => theme.custom.ui.primaryActionText,

            '&:hover': {
              backgroundColor: (theme) => theme.custom.ui.primaryActionHover,
            },
          }}
        >
          Buscar
        </Button>

      </Stack>


      {/* Configuración MIU DataGrid */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 210px)', // ajusta según tu layout
          minHeight: 0,
        }}
       >
        <DataGrid
          rows={datos}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          density="compact"
          rowHeight={38}
          headerHeight={40}
          pagination
          disableRowSelectionOnClick

          paginationMode="server"
          rowCount={rowCount}
          pageSizeOptions={[10, 20, 50, 100]}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={({ page: newPage, pageSize: newPageSize }) => {
            const isPageSizeChange = newPageSize !== pageSize;

            const finalPage = isPageSizeChange ? 0 : newPage;

            setPage(finalPage);
            setPageSize(newPageSize);

            fetchDatos({
              page: finalPage,
              pageSize: newPageSize,
            });
          }}

          localeText={esES.components.MuiDataGrid.defaultProps.localeText}
          slots={{
            toolbar: CustomToolbar,
            loadingOverlay: DataGridSkeleton,
          }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 400 },
            },
          }}
          sx={{
            height: '100%',
            width: '100%',
            minWidth: 720,      // scroll horizontal cuando corresponde
            border: 'none',
            // accesibilidad / foco
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
          }}
        />

        <Typography variant="caption" color="text.secondary">
          {rowCount.toLocaleString('es-CL')} registros encontrados
        </Typography>

      </Box>
    </Box>
  );
};

export default Seguimiento;
